import { Permission } from '@bookora/shared';
import { useMutation, useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query';

import type {
  ClinicOffersResponse,
  ClinicVerificationStateResponse,
  DirectoryPreviewResponse,
  OnboardingStateResponse,
  PlanResponse,
  RoomWithBranchResponse as RoomResponse,
  WalletResponse,
} from '@/api/generated/model';
import { queryKeys } from '@/api/query-keys';
import { useAuth } from '@/auth/token-store';
import { grantRefusalMessage } from '@/features/staff/hooks';
import { readGrantRefusal } from '@/features/staff/permission-model';
import { toast } from '@/lib/feedback';
import { useCan, useCanAll } from '@/permissions/use-can';

import {
  fetchBillingState,
  fetchClinic,
  fetchDirectoryPreview,
  fetchImportFormat,
  fetchInvoices,
  fetchOnboarding,
  fetchPlans,
  fetchRefundEligibility,
  fetchRooms,
  fetchStarterKits,
  fetchSubscriptionWallet,
  fetchSubscriptionWalletTransactions,
  fetchTreatedPatients,
  fetchVerificationState,
  fetchWallet,
  fetchWalletTransactions,
  offersApi,
  starterKitApi,
  type BillingState,
  type Clinic,
  type Invoice,
  type InvoiceKind,
  type PagedResult,
  type PatientImportFormat,
  type RefundEligibility,
  type StarterKitPreview,
  type StarterKitSummary,
  type TreatedPatientsResponse,
  type WalletTransaction,
} from './api';

/**
 * The settings section's reads, each behind the permission that guards its
 * endpoint on the server.
 *
 * `settings.billing` is the interesting one: it gates the subscription, the
 * invoices AND the SMS wallet, so a clinic can hand «تنظیمات» to an office
 * manager without handing over the card. A query the caller cannot make is
 * never issued — no wasted round trip and no 403 in the console that looks
 * like a bug during a demo.
 */
export const settingsKeys = {
  all: (clinicId: string | null) => ['settings-section', clinicId] as const,
  clinic: (clinicId: string | null) => ['settings-section', clinicId, 'clinic'] as const,
  onboarding: (clinicId: string | null) => ['settings-section', clinicId, 'onboarding'] as const,
  directory: (clinicId: string | null) => ['settings-section', clinicId, 'directory'] as const,
  rooms: (clinicId: string | null) => ['settings-section', clinicId, 'rooms'] as const,
  verification: (clinicId: string | null) =>
    ['settings-section', clinicId, 'verification'] as const,
  billing: (clinicId: string | null) => ['settings-section', clinicId, 'billing'] as const,
  offers: (clinicId: string | null) => ['settings-section', clinicId, 'offers'] as const,
  plans: (clinicId: string | null) => ['settings-section', clinicId, 'plans'] as const,
  invoices: (clinicId: string | null, page: number, kind: InvoiceKind | null = null) =>
    ['settings-section', clinicId, 'invoices', page, kind] as const,
  wallet: (clinicId: string | null) => ['settings-section', clinicId, 'wallet'] as const,
  walletTransactions: (clinicId: string | null, page: number) =>
    ['settings-section', clinicId, 'wallet-tx', page] as const,
  subscriptionWallet: (clinicId: string | null) =>
    ['settings-section', clinicId, 'subscription-wallet'] as const,
  subscriptionWalletTransactions: (clinicId: string | null, page: number) =>
    ['settings-section', clinicId, 'subscription-wallet-tx', page] as const,
  starterKits: (clinicId: string | null) => ['settings-section', clinicId, 'starter-kits'] as const,
  starterKitPreview: (clinicId: string | null, key: string) =>
    ['settings-section', clinicId, 'starter-kit', key] as const,
  importFormat: (clinicId: string | null) =>
    ['settings-section', clinicId, 'import-format'] as const,
} as const;

export function useClinic(): UseQueryResult<Clinic> {
  const { activeClinicId } = useAuth();
  const canView = useCan(Permission.SETTINGS_VIEW);
  return useQuery({
    queryKey: settingsKeys.clinic(activeClinicId),
    queryFn: ({ signal }) => fetchClinic(signal),
    enabled: canView && Boolean(activeClinicId),
    staleTime: 60_000,
  });
}

export function useOnboarding(): UseQueryResult<OnboardingStateResponse> {
  const { activeClinicId } = useAuth();
  const canView = useCan(Permission.SETTINGS_VIEW);
  return useQuery({
    queryKey: settingsKeys.onboarding(activeClinicId),
    queryFn: ({ signal }) => fetchOnboarding(signal),
    enabled: canView && Boolean(activeClinicId),
    staleTime: 30_000,
  });
}

export function useDirectoryPreview(): UseQueryResult<DirectoryPreviewResponse> {
  const { activeClinicId } = useAuth();
  // `directory.manage`, not `settings.view`:
  // `backend/src/public/controllers/directory-preview.controller.ts:49` guards
  // `GET /directory/preview` with `DIRECTORY_MANAGE`. Gating on the wrong key
  // meant a `settings.view`-only user fired a guaranteed 403 and then watched
  // the whole «صفحهٔ عمومی کلینیک» block disappear with no explanation —
  // against this file's own contract that a query the caller cannot make is
  // never issued.
  const canPreview = useCan(Permission.DIRECTORY_MANAGE);
  return useQuery({
    queryKey: settingsKeys.directory(activeClinicId),
    queryFn: ({ signal }) => fetchDirectoryPreview(signal),
    enabled: canPreview && Boolean(activeClinicId),
    staleTime: 60_000,
  });
}

export function useVerificationState(): UseQueryResult<ClinicVerificationStateResponse> {
  const { activeClinicId } = useAuth();
  const canView = useCan(Permission.SETTINGS_VIEW);
  return useQuery({
    queryKey: settingsKeys.verification(activeClinicId),
    queryFn: ({ signal }) => fetchVerificationState(signal),
    enabled: canView && Boolean(activeClinicId),
    staleTime: 30_000,
  });
}

export function useRooms(): UseQueryResult<RoomResponse[]> {
  const { activeClinicId } = useAuth();
  const canView = useCan(Permission.SETTINGS_VIEW);
  return useQuery({
    queryKey: settingsKeys.rooms(activeClinicId),
    queryFn: ({ signal }) => fetchRooms(signal),
    enabled: canView && Boolean(activeClinicId),
    staleTime: 60_000,
  });
}

export function useBillingState(): UseQueryResult<BillingState> {
  const { activeClinicId } = useAuth();
  const canBilling = useCan(Permission.SETTINGS_BILLING);
  return useQuery({
    queryKey: settingsKeys.billing(activeClinicId),
    queryFn: ({ signal }) => fetchBillingState(signal),
    enabled: canBilling && Boolean(activeClinicId),
    staleTime: 60_000,
  });
}

/** The clinic's offers; `enabled` lets a caller hold the read back (impersonation). */
export function useClinicOffers(
  options: { enabled?: boolean } = {},
): UseQueryResult<ClinicOffersResponse> {
  const { activeClinicId } = useAuth();
  const canBilling = useCan(Permission.SETTINGS_BILLING);
  return useQuery({
    queryKey: settingsKeys.offers(activeClinicId),
    queryFn: ({ signal }) => offersApi.list(signal),
    enabled: canBilling && Boolean(activeClinicId) && (options.enabled ?? true),
    staleTime: 60_000,
  });
}

export function usePlans(): UseQueryResult<PlanResponse[]> {
  const { activeClinicId } = useAuth();
  const canBilling = useCan(Permission.SETTINGS_BILLING);
  return useQuery({
    queryKey: settingsKeys.plans(activeClinicId),
    queryFn: ({ signal }) => fetchPlans(signal),
    enabled: canBilling && Boolean(activeClinicId),
    staleTime: 10 * 60_000,
  });
}

/**
 * The clinic's invoices, newest first. `kind` narrows to one kind (the results
 * tab lists `RAAS_STATEMENT` only); without it every invoice is listed.
 */
export function useInvoices(
  page: number,
  kind: InvoiceKind | null = null,
): UseQueryResult<PagedResult<Invoice>> {
  const { activeClinicId } = useAuth();
  const canBilling = useCan(Permission.SETTINGS_BILLING);
  return useQuery({
    queryKey: settingsKeys.invoices(activeClinicId, page, kind),
    queryFn: ({ signal }) =>
      fetchInvoices({ page, pageSize: 20, ...(kind ? { kind } : {}) }, signal),
    enabled: canBilling && Boolean(activeClinicId),
    staleTime: 60_000,
  });
}

export function useWallet(): UseQueryResult<WalletResponse> {
  const { activeClinicId } = useAuth();
  const canBilling = useCan(Permission.SETTINGS_BILLING);
  return useQuery({
    queryKey: settingsKeys.wallet(activeClinicId),
    queryFn: ({ signal }) => fetchWallet(signal),
    enabled: canBilling && Boolean(activeClinicId),
    staleTime: 30_000,
  });
}

export function useWalletTransactions(
  page: number,
): UseQueryResult<PagedResult<WalletTransaction>> {
  const { activeClinicId } = useAuth();
  const canBilling = useCan(Permission.SETTINGS_BILLING);
  return useQuery({
    queryKey: settingsKeys.walletTransactions(activeClinicId, page),
    queryFn: ({ signal }) => fetchWalletTransactions({ page, pageSize: 20 }, signal),
    enabled: canBilling && Boolean(activeClinicId),
    staleTime: 30_000,
  });
}

/**
 * The subscription wallet. `enabled: false` lets a caller that only sometimes
 * needs it (the shell's overdraft banner) keep the hook order without the read.
 */
export function useSubscriptionWallet({
  enabled = true,
}: { enabled?: boolean } = {}): UseQueryResult<WalletResponse> {
  const { activeClinicId } = useAuth();
  const canBilling = useCan(Permission.SETTINGS_BILLING);
  return useQuery({
    queryKey: settingsKeys.subscriptionWallet(activeClinicId),
    queryFn: ({ signal }) => fetchSubscriptionWallet(signal),
    enabled: enabled && canBilling && Boolean(activeClinicId),
    staleTime: 30_000,
  });
}

/** `GET /billing/refund` — the money-back window, and a request already made. */
export function useRefundEligibility(): UseQueryResult<RefundEligibility> {
  const { activeClinicId } = useAuth();
  const canBilling = useCan(Permission.SETTINGS_BILLING);
  return useQuery({
    queryKey: queryKeys.billing.refund(activeClinicId),
    queryFn: ({ signal }) => fetchRefundEligibility(signal),
    enabled: canBilling && Boolean(activeClinicId),
    staleTime: 60_000,
    retry: false,
  });
}

/**
 * Treated patients per Jalali month, for the calculator's seed. Open to FLAT
 * clinics too; a failure only means the calculator starts from the rate's
 * included quantity.
 */
export function useTreatedPatients(months = 3): UseQueryResult<TreatedPatientsResponse> {
  const { activeClinicId } = useAuth();
  const canBilling = useCan(Permission.SETTINGS_BILLING);
  return useQuery({
    queryKey: queryKeys.billing.treatedPatients(activeClinicId, months),
    queryFn: ({ signal }) => fetchTreatedPatients(months, signal),
    enabled: canBilling && Boolean(activeClinicId),
    staleTime: 10 * 60_000,
    retry: false,
  });
}

export function useSubscriptionWalletTransactions(
  page: number,
): UseQueryResult<PagedResult<WalletTransaction>> {
  const { activeClinicId } = useAuth();
  const canBilling = useCan(Permission.SETTINGS_BILLING);
  return useQuery({
    queryKey: settingsKeys.subscriptionWalletTransactions(activeClinicId, page),
    queryFn: ({ signal }) => fetchSubscriptionWalletTransactions({ page, pageSize: 20 }, signal),
    enabled: canBilling && Boolean(activeClinicId),
    staleTime: 30_000,
  });
}

/* -------------------------------------------------------------------------- */
/* Onboarding                                                                 */
/* -------------------------------------------------------------------------- */

export function useStarterKits(): UseQueryResult<StarterKitSummary[]> {
  const { activeClinicId } = useAuth();
  const canView = useCan(Permission.SETTINGS_VIEW);
  return useQuery({
    queryKey: settingsKeys.starterKits(activeClinicId),
    queryFn: ({ signal }) => fetchStarterKits(signal),
    enabled: canView && Boolean(activeClinicId),
    staleTime: 30 * 60_000,
  });
}

/**
 * The dry-run preview of a kit against THIS clinic.
 *
 * It writes nothing, and it is what makes «اعمال» honest: applying is
 * skip-existing with no overwrite flag, so the only useful thing to show an
 * owner beforehand is which rows would be created and which already exist and
 * would be left alone. Collapsing that to one number hides the half that
 * matters to a clinic that has already started entering its own tariffs.
 */
export function useStarterKitPreview(key: string | null): UseQueryResult<StarterKitPreview> {
  const { activeClinicId } = useAuth();
  const canView = useCan(Permission.SETTINGS_VIEW);
  return useQuery({
    queryKey: settingsKeys.starterKitPreview(activeClinicId, key ?? ''),
    queryFn: ({ signal }) => starterKitApi.preview(key as string, signal),
    enabled: canView && Boolean(key) && Boolean(activeClinicId),
    staleTime: 60_000,
  });
}

/**
 * Applying a kit needs BOTH keys — the server requires them together.
 *
 * And holding both is NOT sufficient. A kit creates clinic-owned ROLES, so
 * `StarterKitService` runs the union of those roles' permissions through the
 * grant ceiling; neither `settings.edit` nor `procedure.manage` implies any
 * finance or photo key, so an office manager can hold both and still be
 * refused with `PERMISSION_GRANT_EXCEEDS_OWN`. That is why this hook gates the
 * button and {@link useSettingsMutation} still has to name the refused keys.
 */
export function useCanApplyStarterKit(): boolean {
  return useCanAll([Permission.SETTINGS_EDIT, Permission.PROCEDURE_MANAGE]);
}

export function useImportFormat(): UseQueryResult<PatientImportFormat> {
  const { activeClinicId } = useAuth();
  const canImport = useCan(Permission.PATIENT_CREATE);
  return useQuery({
    queryKey: settingsKeys.importFormat(activeClinicId),
    queryFn: ({ signal }) => fetchImportFormat(signal),
    enabled: canImport && Boolean(activeClinicId),
    staleTime: 60 * 60_000,
  });
}

/* -------------------------------------------------------------------------- */
/* Writes                                                                     */
/* -------------------------------------------------------------------------- */

export interface SettingsMutationOptions<TResult = unknown> {
  /**
   * Persian sentence shown on success — or, when what happened depends on the
   * answer (a plan change that raised an invoice vs. one scheduled for the
   * period's end), a function of the result. `null` shows no toast, for a
   * success the screen already answers with something larger.
   */
  successMessage: string | ((result: TResult) => string | null);
  onDone?: () => void;
  /** The screen shows the failure itself (a field error); skip the error toast. */
  quietError?: boolean;
  /** The keys the grant ceiling refused, for a screen that can point at them. */
  onGrantRefused?: (refusal: { labels: string[]; count: number }) => void;
}

/**
 * A settings write, with the toast and the invalidation attached.
 *
 * Coarse invalidation, for a concrete reason: subscribing to a plan changes
 * the seat limit, the SMS bundle, the invoice list AND whether the clinic is
 * read-only; applying a starter kit changes the onboarding checklist, the
 * rooms and the tariff list at once. Anything narrower leaves one of those
 * stale on screen right after the action that changed it.
 */
export function useSettingsMutation<TArgs, TResult>(
  mutationFn: (args: TArgs) => Promise<TResult>,
  { successMessage, onDone, onGrantRefused, quietError }: SettingsMutationOptions<TResult>,
) {
  const queryClient = useQueryClient();
  const { activeClinicId } = useAuth();

  return useMutation({
    mutationFn,
    onSuccess: (result: TResult) => {
      const text = typeof successMessage === 'function' ? successMessage(result) : successMessage;
      if (text) toast.success(text);
      void queryClient.invalidateQueries({ queryKey: settingsKeys.all(activeClinicId) });
      // The pricing build's reads (`queryKeys.billing.*`: plan access, the RaaS
      // meter and charges, the refund window) change with the same writes — a
      // plan change moves the locks, a dispute moves the charges table.
      void queryClient.invalidateQueries({ queryKey: ['billing', activeClinicId] });
      onDone?.();
    },
    onError: (error: unknown) => {
      // Starter-kit apply reaches the grant ceiling, because a kit creates
      // roles. The reader is the staff feature's, deliberately: one definition
      // of how `PERMISSION_GRANT_EXCEEDS_OWN` is unpacked, so the two sections
      // cannot drift on what the server said.
      const refusal = readGrantRefusal(error);
      if (refusal) {
        toast.errorBanner('این تغییر انجام نشد', new Error(grantRefusalMessage(refusal)));
        onGrantRefused?.(refusal);
        return;
      }
      if (!quietError) toast.error(error);
    },
  });
}
